import React, { useState } from "react";
import { Download, Star, Heart, Smile, Baby, Palette, NotebookPen, StickyNote, MessagesSquare, Sparkles, Wind, Target, School, FileDown, BookOpen, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHero, SectionTitle } from "@/components/Bits";
import { Reveal } from "@/components/Reveal";
import { IMG } from "@/lib/images";
import { downloadBlob, makeBookmarkSVG } from "@/lib/downloads";
import { makeSuperKidPoster, makeRealVsPretend, makeIAmEnoughPoster, makeCalmDownSteps, makeCirclesOfTrust, makeNewSchool } from "@/lib/kidsPrintables";
import coloringLibrary from "@/lib/coloringLibrary.json";
import { toast } from "sonner";
import { PrintableSamples } from "@/components/PrintableSamples";
import { useLang, LangToggle } from "@/lib/lang";
import { SK } from "@/lib/superKidsI18n";
import { toEs } from "@/lib/kidsPrintablesEs";

const BOOKMARKS_ES = [
  { id: "brave", title: "Soy valiente", subtitle: "Separador de héroe", affirm: ["Aunque tenga", "miedo, puedo", "ser valiente.", "", "Un paso.", "Una respiración."], c1: "#1E3E62", c2: "#0B192C" },
  { id: "stars", title: "Las estrellas", subtitle: "brillan en la noche", affirm: ["Las estrellas", "necesitan la", "noche para brillar.", "Yo también.", "", "Mi luz cuenta."], c1: "#2B527E", c2: "#102A45" },
  { id: "voice", title: "Mi voz", subtitle: "importa", affirm: ["Está bien", "sentir lo que", "siento.", "Mi voz importa.", "", "Puedo pedir ayuda."], c1: "#1E3E62", c2: "#1a1033" },
];
const BOOKMARKS = [
  { id: "brave", title: "I Am Brave", subtitle: "Little Hero Bookmark", affirm: ["Even when I'm", "scared, I can", "be brave.", "", "One step.", "One breath."], c1: "#1E3E62", c2: "#0B192C" },
  { id: "stars", title: "Stars Shine", subtitle: "In Dark Skies", affirm: ["Stars need the", "dark to shine.", "", "So do I.", "", "My light counts."], c1: "#2B527E", c2: "#102A45" },
  { id: "voice", title: "My Voice", subtitle: "Matters Bookmark", affirm: ["My feelings are", "okay to feel.", "", "My voice", "matters.", "I can ask for help."], c1: "#1E3E62", c2: "#1a1033" },
];

const FEELINGS = [
  { f: "Happy", c: "#F39E38", e: "😊", msg: "It's wonderful to feel happy! Your joy is a gift.", try: "Share your happy with someone. Tell them one good thing about your day." },
  { f: "Sad", c: "#2B527E", e: "😢", msg: "It's okay to feel sad. Even tears help your heart heal, and sad feelings don't last forever.", try: "Hug a pillow or a pet, and tell a grown-up you trust how you feel." },
  { f: "Angry", c: "#DC2626", e: "😠", msg: "It's okay to feel angry. Anger is a feeling, not a bad thing about you.", try: "Squeeze your fists tight, count to 5, then let go slowly. Do it 3 times." },
  { f: "Scared", c: "#6366F1", e: "😨", msg: "It's okay to feel scared. You are safe and you are brave.", try: "Take 3 slow breaths, and tell a grown-up you trust." },
  { f: "Calm", c: "#0E7490", e: "😌", msg: "Calm feels peaceful, like still water. You found your quiet place.", try: "Notice how your body feels right now, so you can find it again later." },
  { f: "Loved", c: "#D4AF37", e: "🥰", msg: "You are loved, just the way you are. Always.", try: "Think of someone who loves you, and give yourself a big hug." },
];

const COLORING_CATS = coloringLibrary;

const sheet = (grp, n, label, pg = "Page") => ({ id: `${grp}-${n}`, title: `${label} · ${pg} ${n}`, img: `/love_notes/${grp}_page_${n}.jpg`, pdf: `/love_notes/BSN_${grp}_love_notes_page_${n}.pdf` });
const pages = (grp, label, pg, count = 4) => Array.from({ length: count }, (_, i) => sheet(grp, i + 1, label, pg));
const noteGroups = (s) => [
  ...(s.esGroups || []).map((g) => ({ ...g, pdf: `/love_notes/BSN_${g.id}_love_notes_page_1.pdf`, sheets: pages(g.id, g.label, s.page, 1) })),
  ...["kids", "teens"].map((id) => ({ id, title: s.groups[id][0], desc: s.groups[id][1], pdf: `/love_notes/BSN_${id}_love_notes_all.pdf`, sheets: pages(id, s.groups[id][2], s.page) })),
];

const thumb = (svg, w = 150, h = 194) =>
  svg.replace('width="816" height="1056"', `width="${w}" height="${h}"`);

export default function SuperKids() {
  const [activeCat, setActiveCat] = useState(COLORING_CATS[0].id);
  const [feeling, setFeeling] = useState(null);
  const { lang } = useLang();
  const s = SK[lang];
  const feelings = FEELINGS.map((f, i) => ({ ...f, key: f.f.toLowerCase(), f: s.feelings[i][0], msg: s.feelings[i][1], try: s.feelings[i][2] }));
  const tr = (x) => (lang === "es" ? toEs(x) : x);
  const bookmarks = lang === "es" ? BOOKMARKS_ES : BOOKMARKS;
  const done = lang === "es" ? "Se está descargando. ¡Imprime y disfruta!" : "is downloading — print & enjoy!";
  const cat = COLORING_CATS.find((c) => c.id === activeCat) || COLORING_CATS[0];

  const download = (b) => {
    downloadBlob(`BSN-Bookmark-${b.id}.svg`, tr(makeBookmarkSVG(b)), "image/svg+xml");
    toast.success(`"${b.title}" ${done}`);
  };

  const downloadPledge = () => {
    downloadBlob(s.pledgeFile, s.pledge, "text/plain");
    toast.success(s.pledgeToast);
  };

  const downloadColoringSet = (c) => {
    toast.success(`"${c.label}" ${done}`);
  };
  const downloadPoster = () => {
    downloadBlob("BSN-Why-I-Am-A-Super-Kid.svg", tr(makeSuperKidPoster()), "image/svg+xml");
    toast.success(`"${s.posterTitle}" ${done}`);
  };
  const downloadWorksheet = () => {
    downloadBlob("BSN-Real-and-Make-Believe.html", tr(makeRealVsPretend()), "text/html");
    toast.success(`"${s.gcTitle}" ${done}`);
  };

  const TOOLKIT = [
    { id: "enough", icon: Sparkles, title: lang === "es" ? "Soy suficiente" : "I Am Enough", desc: lang === "es" ? "Un póster dorado y azul con líneas para completar: 'Tal como soy, soy suficiente.'" : "A gold-and-navy affirmation poster with fill-in lines: 'Just as I am, I am enough.'", make: makeIAmEnoughPoster, ext: "svg", type: "image/svg+xml" },
    { id: "calm", icon: Wind, title: lang === "es" ? "Mis pasos para calmarme" : "My Calm-Down Steps", desc: lang === "es" ? "Cinco pasos suaves: notar, respirar, ponerle nombre, elegir una herramienta y pedir ayuda, con espacio para sus propias herramientas." : "Five gentle steps — Notice, Breathe, Name it, Choose a tool, Ask for help — with space for their own tools.", make: makeCalmDownSteps, ext: "html", type: "text/html" },
    { id: "circles", icon: Target, title: lang === "es" ? "Mis círculos de confianza" : "My Circles of Trust", desc: lang === "es" ? "Tres círculos: los más cercanos y seguros, amigos y compañeros, y personas que conozco de nombre, con ejemplos y una guía de seguro vs. cuidado para llenar juntos." : "Three circles — closest & safest, friends & classmates, people I know by name — with examples and a safe-vs-careful guide to fill in together.", make: makeCirclesOfTrust, ext: "svg", type: "image/svg+xml" },
    { id: "newschool", icon: School, title: lang === "es" ? "Una escuela nueva: ¿y ahora qué?" : "A New School — Now What?", desc: lang === "es" ? "Elegir amigos de verdad, ir despacio con los niños nuevos y saber cuándo contarle a un adulto de confianza sobre un problema." : "Choosing real friends, going slow with new kids, and knowing when to tell a trusted adult about a problem.", make: makeNewSchool, ext: "html", type: "text/html" },
  ];
  const downloadToolkit = (t) => {
    downloadBlob(`BSN-${t.id}${lang === "es" ? "-es" : ""}.${t.ext}`, tr(t.make()), t.type);
    toast.success(`"${t.title}" ${done}`);
  };

  return (
    <div data-testid="super-kids-page" data-i18n="manual">
      <PageHero
        eyebrow={s.heroEyebrow}
        title={s.heroTitle}
        subtitle={s.heroSub}
        image={IMG.kidsJoy}
        testid="super-kids-hero"
      >
        <LangToggle />
      </PageHero>

      <section className="max-w-5xl mx-auto px-4 pt-16" data-testid="teen-facts-callout">
        <Link to="/teen-facts" className="group flex flex-col sm:flex-row sm:items-center gap-5 rounded-3xl bg-bsn-midnight text-bsn-parchment border-2 border-bsn-gold/60 hover:border-bsn-gold p-7 sm:p-9" data-testid="teen-facts-link">
          <BookOpen className="w-10 h-10 text-bsn-gold shrink-0" />
          <span className="flex-1">
            <span className="block eyebrow text-bsn-gold mb-1">{s.teenEyebrow}</span>
            <span className="block font-serif text-2xl sm:text-3xl font-semibold">{s.teenTitle}</span>
            <span className="block mt-1 text-sm text-bsn-ice/75">{s.teenSub}</span>
          </span>
          <span className="inline-flex items-center gap-1 font-semibold text-bsn-gold">{s.teenCta} <ArrowRight className="w-4 h-4" /></span>
        </Link>
        <Link to="/storm-watchers" className="mt-4 flex flex-col sm:flex-row sm:items-center gap-4 rounded-3xl bg-white border-2 border-bsn-gold/50 hover:border-bsn-gold p-6 sm:p-7 card-tactile" data-testid="storm-watchers-link">
          <span className="flex-1">
            <span className="block eyebrow mb-1">{s.swEyebrow}</span>
            <span className="block font-serif text-2xl font-semibold text-bsn-midnight">Storm Watchers</span>
            <span className="block mt-1 text-sm text-bsn-sapphire/75">{s.swSub}</span>
          </span>
          <span className="inline-flex items-center gap-1 font-semibold text-bsn-bronze">{s.swCta} <ArrowRight className="w-4 h-4" /></span>
        </Link>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-16 sm:py-20">
        <SectionTitle eyebrow={s.bmEyebrow} title={s.bmTitle} subtitle={s.bmSub + s.englishOnly} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {bookmarks.map((b, i) => (
            <Reveal key={b.id} delay={i * 90}>
              <div className="bg-white rounded-2xl p-6 card-tactile flex flex-col items-center" data-testid={`bookmark-card-${b.id}`}>
                <div className="rounded-xl overflow-hidden shadow-lg" style={{ width: 128, height: 256 }}
                  dangerouslySetInnerHTML={{ __html: tr(makeBookmarkSVG(b)).replace('width="192" height="576"', 'width="128" height="256"') }} />
                <h4 className="font-serif text-xl font-semibold text-bsn-midnight mt-5">{b.title}</h4>
                <p className="text-sm text-bsn-sapphire/70">{b.subtitle}</p>
                <button onClick={() => download(b)} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-gold font-semibold text-sm transition-all" data-testid={`download-bookmark-${b.id}`}>
                  <Download className="w-4 h-4" /> Download Free
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Coloring Corner — categorized library */}
      <section className="bg-bsn-canvas border-y border-bsn-gold/20" data-testid="coloring-section">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:py-20">
          <SectionTitle eyebrow={s.colEyebrow} title={s.colTitle} subtitle={s.colSub} />

          {/* Category tabs */}
          <div className="flex flex-wrap gap-2 justify-center mb-10" data-testid="coloring-tabs">
            {COLORING_CATS.map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveCat(c.id)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${activeCat === c.id ? "bg-bsn-midnight text-bsn-parchment" : "bg-white text-bsn-sapphire card-tactile hover:bg-white/70"}`}
                data-testid={`coloring-tab-${c.id}`}
              >
                {c.label}{c.count > 0 && <span className="opacity-60"> · {c.count}</span>}
              </button>
            ))}
          </div>

          {cat.pages.length === 0 && (
            <div className="bg-white rounded-2xl p-10 card-tactile text-center max-w-2xl mx-auto" data-testid="coloring-coming-soon">
              <Palette className="w-10 h-10 text-bsn-bronze mx-auto mb-3" />
              <h4 className="font-serif text-2xl font-semibold text-bsn-midnight">{s.soonTitle}</h4>
              <p className="text-sm text-bsn-sapphire/75 mt-2">{s.soonSub(cat.label)}</p>
            </div>
          )}

          {/* Full-set PDF download */}
          {cat.pdf && <Reveal>
            <div className="bg-bsn-midnight text-bsn-parchment rounded-2xl p-6 sm:p-7 flex flex-col sm:flex-row items-center justify-between gap-4 mb-10 border border-bsn-gold/25" data-testid="coloring-set-banner">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-bsn-gold/20 flex items-center justify-center shrink-0">
                  <Palette className="w-6 h-6 text-bsn-gold" />
                </div>
                <div>
                  <h4 className="font-serif text-xl font-semibold text-bsn-champagne">{s.setTitle(cat.label)}</h4>
                  <p className="text-sm text-bsn-ice/75">{s.setSub(cat.count)}</p>
                </div>
              </div>
              <a
                href={cat.pdf}
                download
                onClick={() => downloadColoringSet(cat)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full btn-gold font-semibold text-sm transition-all shrink-0"
                data-testid={`download-coloring-set-${cat.id}`}
              >
                <FileDown className="w-4 h-4" /> {s.setBtn}
              </a>
            </div>
          </Reveal>}

          {/* Individual pages grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="coloring-grid">
            {cat.pages.map((p, i) => (
              <Reveal key={p.file} delay={(i % 4) * 60}>
                <div className="bg-white rounded-2xl p-3 card-tactile flex flex-col items-center h-full" data-testid={`coloring-page-${cat.id}-${i}`}>
                  <div className="rounded-lg overflow-hidden border border-bsn-gold/20 bg-white w-full aspect-[3/4]">
                    <img src={p.file} alt={`${p.title} coloring page`} loading="lazy" className="w-full h-full object-contain" />
                  </div>
                  <h4 className="font-serif text-base font-semibold text-bsn-midnight mt-3 text-center leading-tight">{p.title}</h4>
                  <a
                    href={p.pdf || p.file}
                    download
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full btn-gold font-semibold text-xs transition-all"
                    data-testid={`download-coloring-page-${cat.id}-${i}`}
                  >
                    <Download className="w-3.5 h-3.5" /> {s.printColor}
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>


      {/* Carry-to-School Printables */}
      <section className="max-w-7xl mx-auto px-4 py-16 sm:py-20" data-testid="carry-to-school-section">
        <SectionTitle eyebrow={s.kitEyebrow} title={s.kitTitle} subtitle={s.kitSub} />
        <div className="max-w-3xl">
          <Reveal>
            <div className="h-full bg-white rounded-2xl p-7 card-tactile flex gap-6 items-center" data-testid="poster-card">
              <div className="rounded-lg overflow-hidden border border-bsn-gold/20 shrink-0" dangerouslySetInnerHTML={{ __html: thumb(tr(makeSuperKidPoster()), 170, 220) }} />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1"><NotebookPen className="w-5 h-5 text-bsn-bronze" /><h4 className="font-serif text-xl font-semibold text-bsn-midnight">{s.posterTitle}</h4></div>
                <p className="text-sm text-bsn-sapphire/75 leading-relaxed">{s.posterDesc}{s.englishOnly}</p>
                <button onClick={downloadPoster} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-gold font-semibold text-sm transition-all" data-testid="download-poster">
                  <Download className="w-4 h-4" /> {s.posterBtn}
                </button>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="mt-16" data-testid="lunchbox-notes-section">
          <div className="flex items-center gap-2 mb-2"><StickyNote className="w-5 h-5 text-bsn-bronze" /><h3 className="font-serif text-2xl font-semibold text-bsn-midnight">{s.lnTitle}</h3></div>
          <p className="text-bsn-sapphire/75 mb-8 max-w-2xl">{s.lnSub}</p>
          <PrintableSamples groups={noteGroups(s)} />
        </div>
      </section>

      <section className="bg-bsn-canvas border-y border-bsn-gold/20">
        <div className="max-w-5xl mx-auto px-4 py-16">
          <SectionTitle eyebrow={s.fwEyebrow} title={s.fwTitle} subtitle={s.fwSub} />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="feelings-wheel">
            {feelings.map((f, i) => (
              <Reveal key={f.key} delay={i * 60}>
                <button
                  type="button"
                  onClick={() => setFeeling(feeling?.f === f.f ? null : f)}
                  aria-pressed={feeling?.f === f.f}
                  className="w-full rounded-2xl p-6 text-center card-tactile hover:-translate-y-1 border-2"
                  style={{ backgroundColor: `${f.c}12`, borderColor: feeling?.f === f.f ? f.c : "transparent" }}
                  data-testid={`feeling-${f.key}`}
                >
                  <div className="text-4xl mb-2">{f.e}</div>
                  <p className="font-serif text-lg font-semibold" style={{ color: f.c }}>{f.f}</p>
                </button>
              </Reveal>
            ))}
          </div>
          <div aria-live="polite" className="mt-6">
            {feeling ? (
              <div className="bg-white rounded-2xl p-6 sm:p-8 card-tactile border-l-4 flex gap-5 items-start" style={{ borderLeftColor: feeling.c }} data-testid="feeling-message">
                <div className="text-5xl shrink-0">{feeling.e}</div>
                <div>
                  <p className="font-serif text-xl sm:text-2xl font-semibold text-bsn-midnight leading-snug" data-testid="feeling-message-text">{feeling.msg}</p>
                  <p className="mt-3 text-base text-bsn-sapphire/85"><span className="font-semibold" style={{ color: feeling.c }}>{s.tryThis}</span>{feeling.try}</p>
                </div>
              </div>
            ) : (
              <p className="text-center text-sm text-bsn-sapphire/60" data-testid="feeling-hint">{s.fwHint}</p>
            )}
          </div>
        </div>
      </section>

      {/* Big-Feelings Toolkit */}
      <section className="max-w-7xl mx-auto px-4 py-16 sm:py-20" data-testid="toolkit-section">
        <SectionTitle eyebrow={s.tkEyebrow} title={s.tkTitle} subtitle={s.tkSub + s.englishOnly} />
        <div className="grid sm:grid-cols-2 gap-6">
          {TOOLKIT.map((t, i) => (
            <Reveal key={t.id} delay={i * 80}>
              <div className="h-full bg-white rounded-2xl p-7 card-tactile flex gap-5 items-start" data-testid={`toolkit-card-${t.id}`}>
                <div className="w-14 h-14 rounded-xl bg-bsn-gold/15 flex items-center justify-center shrink-0">
                  <t.icon className="w-7 h-7 text-bsn-bronze" />
                </div>
                <div className="flex-1">
                  <h4 className="font-serif text-xl font-semibold text-bsn-midnight">{t.title}</h4>
                  <p className="text-sm text-bsn-sapphire/75 leading-relaxed mt-1.5">{t.desc}</p>
                  <button onClick={() => downloadToolkit(t)} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-gold font-semibold text-sm transition-all" data-testid={`download-toolkit-${t.id}`}>
                    <Download className="w-4 h-4" /> Download Free
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Gentle Conversations worksheet */}
      <section className="max-w-4xl mx-auto px-4 py-16 sm:py-20" data-testid="gentle-conversations-section">
        <div className="bg-white rounded-3xl p-8 sm:p-12 card-tactile">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-bsn-gold/15 flex items-center justify-center"><MessagesSquare className="w-6 h-6 text-bsn-bronze" /></div>
            <div>
              <p className="eyebrow">{s.gcEyebrow}</p>
              <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-bsn-midnight">{s.gcTitle}</h3>
            </div>
          </div>
          <p className="text-bsn-sapphire/85 leading-relaxed">{s.gcBody}{s.englishOnly}</p>
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 leading-relaxed">
            <strong>{s.gcNoteB}</strong> {s.gcNote}
          </div>
          <button onClick={downloadWorksheet} className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full btn-gold font-semibold transition-all" data-testid="download-worksheet">
            <Download className="w-5 h-5" /> {s.gcBtn}
          </button>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 py-16 sm:py-20">
        <div className="bg-bsn-midnight text-bsn-parchment rounded-3xl p-8 sm:p-12 text-center border border-bsn-gold/25">
          <div className="flex justify-center gap-2 mb-5">
            <Star className="w-7 h-7 text-bsn-gold" /><Heart className="w-7 h-7 text-bsn-gold" /><Smile className="w-7 h-7 text-bsn-gold" />
          </div>
          <h3 className="font-serif text-2xl sm:text-3xl font-semibold">{s.plTitle}</h3>
          <p className="mt-4 text-bsn-ice/80 leading-relaxed max-w-xl mx-auto">{s.plSub}</p>
          <button onClick={downloadPledge} className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full btn-gold font-semibold transition-all" data-testid="download-pledge-btn">
            <Download className="w-5 h-5" /> {s.plBtn}
          </button>
        </div>
      </section>
    </div>
  );
}
